import { AppsyncRepositoryBase, handleError } from './appsyncRepositoryBase';

import type { EmptyJsonPub, EmptyPropsPub } from './entityBase';
import type { EntityDataPubType, EntityDataType } from '@/infra/appsync';

import { getSdk } from '@/infra/appsync';

/**
 * Entity保存用レポジトリの抽象クラス.
 * publicの取得もある(TODO: AppsyncRepositoryPublicに分けた方がいい).
 */
export abstract class AppsyncRepositoryPvt<
  E, P, J
> extends AppsyncRepositoryBase<E, P, J, EmptyPropsPub, EmptyJsonPub> {
  /**
   * createFromEntityDataType
   */
  protected abstract createFromEntityDataType: (props: EntityDataType) => E;

  /**
   * リスト取得
   * @param nextToken 次のトークン
   * @param limit 上限
   * @returns 結果
   */
  async getList(nextToken: string | null, limit = 25): Promise<{
    /** 次のトークン */
    nextToken: string,
    /** アイテムリスト */
    items: E[],
  }> {
    const client = await this.cognitoApi.getGraphQLClient();
    const sdk = getSdk(client);
    const result = await this.retryGetDataEntities(
      () => sdk.GetDataEntities({
        entityName: this.entityName,
        limit,
        nextToken,
      }),
      this.entityName,
    );
    if (result.errorObj) {
      throw handleError(result.errorObj);
    }
    const resultEntity = result.response?.getEntities;
    if (!resultEntity || !resultEntity.items) {
      throw Error(`Error in ${this.entityName}/getList: not found property`);
    }
    const items = resultEntity.items.map(
      (v) => this.createFromEntityDataType(v),
    );
    return {
      nextToken: resultEntity.nextToken || '',
      items,
    };
  }

  /**
   * キーでリスト取得
   * @param key キー
   * @param nextToken 次のトークン
   * @param limit 上限
   * @returns データ
   */
  async getListWithKey(key: string, nextToken: string | null, limit = 25): Promise<{
    /** 次のトークン */
    nextToken: string,
    /** アイテムリスト */
    items: E[],
  }> {
    const client = await this.cognitoApi.getGraphQLClient();
    const sdk = getSdk(client);
    const result = await this.retryGetDataEntitiesWithKey(
      () => sdk.GetDataEntitiesWithKey({
        entityName: this.entityName,
        key,
        limit,
        nextToken,
      }),
      this.entityName,
    );
    if (result.errorObj) {
      throw handleError(result.errorObj);
    }
    const resultEntity = result.response?.getEntitiesWithKey;
    if (!resultEntity || !resultEntity.items) {
      throw Error(`Error in ${this.entityName}/getListWithKey: not found property`);
    }
    const items = resultEntity.items.map(
      (v) => this.createFromEntityDataType(v),
    );
    return {
      nextToken: resultEntity.nextToken || '',
      items,
    };
  }

  /**
   * サブキーでデータ取得
   * @param key キー
   * @param nextToken 次のトークン
   * @param limit 上限
   * @returns データ
   */
  async getListWithSubKey(key: string, nextToken: string | null, limit = 25): Promise<{
    /** 次のトークン */
    nextToken: string,
    /** アイテムリスト */
    items: E[],
  }> {
    const client = await this.cognitoApi.getGraphQLClient();
    const sdk = getSdk(client);
    const result = await this.retryGetDataEntitiesWithSubKey(
      () => sdk.GetDataEntitiesWithSubKey({
        entityName: this.entityName,
        key,
        limit,
        nextToken,
      }),
      this.entityName,
    );
    if (result.errorObj) {
      throw handleError(result.errorObj);
    }
    const resultEntity = result.response?.getEntitiesWithSubKey;
    if (!resultEntity || !resultEntity.items) {
      throw Error(`Error in ${this.entityName}/getListWithSubKey: not found property`);
    }
    const items = resultEntity.items.map(
      (v) => this.createFromEntityDataType(v),
    );
    return {
      nextToken: resultEntity.nextToken || '',
      items,
    };
  }

  /**
   * createFromEntityType
   * @param props props
   * @returns Entity
   */
  protected createFromEntityType = (
    props: EntityDataPubType,
  ) => this.createFromEntityDataType({ data: props.data });

  /**
   * Pubリスト取得
   * @param id ID
   * @param nextToken 次のトークン
   * @param limit 上限
   * @returns データ
   */
  async getPublicList(id: string, nextToken: string | null, limit = 25): Promise<{
    /** 次のトークン */
    nextToken: string,
    /** アイテムリスト */
    items: E[],
  }> {
    const client = await this.cognitoApi.getGraphQLClient();
    const sdk = getSdk(client);
    const result = await this.retryGetPublicEntities(
      () => sdk.GetPublicEntities({
        id,
        entityName: this.entityName,
        limit,
        nextToken,
      }),
      this.entityName,
    );
    if (result.errorObj) {
      throw handleError(result.errorObj);
    }
    const resultEntity = result.response?.getPublicEntities;
    if (!resultEntity || !resultEntity.items) {
      throw Error(`Error in ${this.entityName}/getDataList: not found property`);
    }
    const items = resultEntity.items.map(
      (v) => this.createFromEntityDataType(v),
    );
    return {
      nextToken: resultEntity.nextToken || '',
      items,
    };
  }

  /**
   * 公開全リスト取得
   * @param id ID
   * @param limit 上限
   * @returns データ
   */
  async getPublicAllList(id: string, limit = 25) {
    const items = this.getAllData(async (token: string | null) => {
      const items = await this.getPublicList(id, token, limit);
      return items;
    });
    return items;
  }

  /**
   * キーで公開リスト取得
   * @param id ID
   * @param key キー
   * @param nextToken 次のトークン
   * @param limit 上限
   * @returns データ
   */
  async getPublicListWithKey(
    id: string,
    key: string,
    nextToken: string | null,
    limit = 25,
  ): Promise<{
    /** 次のトークン */
    nextToken: string,
    /** アイテムリスト */
    items: E[],
  }> {
    const client = await this.cognitoApi.getGraphQLClient();
    const sdk = getSdk(client);
    const result = await this.retryGetPublicEntitiesWithKey(
      () => sdk.GetPublicEntitiesWithKey({
        id,
        entityName: this.entityName,
        key,
        limit,
        nextToken,
      }),
      this.entityName,
    );
    if (result.errorObj) {
      throw handleError(result.errorObj);
    }
    const resultEntity = result.response?.getPublicEntitiesWithKey;
    if (!resultEntity || !resultEntity.items) {
      throw Error(`Error in ${this.entityName}/getDataListWithKey: not found property`);
    }
    const items = resultEntity.items.map(
      (v) => this.createFromEntityDataType(v),
    );
    return {
      nextToken: resultEntity.nextToken || '',
      items,
    };
  }

  // filterを使用しているためか、想定した動きになっていないた使用しない方針とする
  // async getPublicListWithSubKey(id: string, key: string, nextToken: string | null, limit = 25) {
  //   const client = await this.cognitoApi.getGraphQLClient();
  //   const sdk = getSdk(client);
  //   const result = await this.retryGetPublicEntitiesWithSubKey(
  //     () => sdk.GetPublicEntitiesWithSubKey({
  //       id,
  //       entityName: this.entityName,
  //       key,
  //       limit,
  //       nextToken,
  //     }),
  //     this.entityName,
  //   );
  //   const resultEntity = result.getPublicEntitiesWithSubKey;
  //   if (!resultEntity || !resultEntity.items) {
  //     throw Error(`Error in ${this.entityName}/getDataListWithSubKey: not found property`);
  //   }
  //   const items = resultEntity.items.map(
  //     (v) => this.createFromEntityDataType(v),
  //   );
  //   return {
  //     nextToken: result.getPublicEntitiesWithSubKey.nextToken || '',
  //     items,
  //   };
  // }
}
